/**
 * Domain types for capture devices. Kept free of any browser/WebRTC or
 * Electron-specific types so they can be safely imported by the main
 * process, preload scripts, and any future native module.
 */

export type DeviceKind = 'videoinput' | 'audioinput';

export interface MediaDeviceDescriptor {
  /** Stable id reported by the platform; may be empty until permission is granted. */
  id: string;
  label: string;
  kind: DeviceKind;
}

export interface CaptureResolution {
  width: number;
  height: number;
}

export const SUPPORTED_FRAME_RATES = [24, 30, 60] as const;
export type FrameRate = (typeof SUPPORTED_FRAME_RATES)[number];

export const SUPPORTED_RESOLUTIONS: CaptureResolution[] = [
  { width: 1280, height: 720 },
  { width: 1920, height: 1080 },
];

export interface CaptureSettings {
  videoDeviceId: string | null;
  audioDeviceId: string | null;
  resolution: CaptureResolution;
  frameRate: FrameRate;
}

export const DEFAULT_CAPTURE_SETTINGS: CaptureSettings = {
  videoDeviceId: null,
  audioDeviceId: null,
  resolution: { width: 1920, height: 1080 },
  frameRate: 60,
};

export type CaptureSessionState =
  'idle' | 'requesting-permissions' | 'active' | 'error' | 'stopped';

export interface CaptureError {
  code:
    | 'permission-denied'
    | 'device-not-found'
    | 'device-in-use'
    | 'constraints-not-satisfiable'
    | 'unknown';
  message: string;
  cause?: unknown;
}
